/**
 * LocationPicker
 *
 * Reusable location input with two modes:
 *   1. Search by name/address (Nominatim free API — no key needed)
 *   2. Pin current GPS location (reverse-geocoded to a real address)
 *
 * Stores: { name, address, lat, lng }
 *   - name: human-readable place name (e.g. "Main Line Health Exton Square")
 *   - address: full street address
 *   - lat/lng: coordinates for Maps links
 *
 * Maps links use the address/name string so Google Maps shows the real place,
 * not raw coordinates.
 */
import React, { useState, useRef } from 'react';
import {
  Box,
  TextField,
  IconButton,
  InputAdornment,
  Typography,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  Button,
  Tooltip,
  Divider,
  Chip,
} from '@mui/material';
import {
  MyLocation as GPSIcon,
  OpenInNew as MapIcon,
  Directions as DirectionsIcon,
  LocationOn as LocationIcon,
  Close as ClearIcon,
} from '@mui/icons-material';
import logger from '../utils/logger';

// Nominatim sometimes returns house_number as a range e.g. "7840;8410" — take the first.
const cleanHouseNumber = (num) => (num ? num.split(';')[0].trim() : null);

// --- Nominatim helpers (free OSM, no API key) ---

const searchPlaces = async (query) => {
  if (!query || query.trim().length < 3) return [];
  try {
    const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&limit=6&countrycodes=us`;
    const resp = await fetch(url, {
      headers: { 'Accept-Language': 'en', 'User-Agent': 'FamilyForLife/1.0' }
    });
    if (!resp.ok) return [];
    return await resp.json();
  } catch {
    return [];
  }
};

const reverseGeocode = async (lat, lng) => {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`;
    const resp = await fetch(url, {
      headers: { 'Accept-Language': 'en', 'User-Agent': 'FamilyForLife/1.0' }
    });
    if (!resp.ok) return null;
    const data = await resp.json();
    const a = data.address || {};
    const parts = [
      cleanHouseNumber(a.house_number),
      a.road || a.pedestrian || a.footway,
      a.neighbourhood || a.suburb,
      a.city || a.town || a.village || a.county,
      a.state,
    ].filter(Boolean);
    return {
      name: data.name || (parts.slice(0, 2).join(' ')) || 'Current location',
      address: parts.join(', ') || data.display_name,
      displayName: data.display_name,
    };
  } catch {
    return null;
  }
};

/** Build a short display label for a Nominatim search result */
const buildLabel = (result) => {
  const a = result.address || {};
  const name = result.name || a.amenity || a.shop || a.tourism || '';
  const road = a.road || a.pedestrian || '';
  const city = a.city || a.town || a.village || a.county || '';
  const state = a.state || '';
  const parts = [name, road, city, state].filter(Boolean);
  // Avoid duplicates in label
  return [...new Set(parts)].join(', ');
};

/** Build a Google Maps URL using address/name (not raw coordinates) */
const buildMapsUrl = (location) => {
  if (!location) return null;
  const query = location.address || location.name;
  if (query) return `https://www.google.com/maps/search/${encodeURIComponent(query)}`;
  if (location.lat) return `https://maps.google.com/?q=${location.lat},${location.lng}`;
  return null;
};

/** Build a Google Maps directions URL */
const buildDirectionsUrl = (location) => {
  if (!location) return null;
  const dest = location.address || location.name;
  if (dest) return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`;
  if (location.lat) return `https://www.google.com/maps/dir/?api=1&destination=${location.lat},${location.lng}`;
  return null;
};

// ---------------------------------------------------------------------------

const LocationPicker = ({
  value,           // { name, address, lat, lng } | null
  onChange,        // (location | null) => void
  label = 'Location',
  placeholder = 'Search for a place or address…',
  disabled = false,
}) => {
  const [searchText, setSearchText] = useState(value?.name || value?.address || '');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [gpsCapturing, setGpsCapturing] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const debounceRef = useRef(null);

  const handleSearchChange = (text) => {
    setSearchText(text);
    if (!text.trim()) {
      setResults([]);
      setShowResults(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      const found = await searchPlaces(text);
      setResults(found);
      setShowResults(true);
      setSearching(false);
    }, 500);
  };

  const handleSelect = (result) => {
    const label = buildLabel(result);
    const a = result.address || {};
    const addrParts = [
      cleanHouseNumber(a.house_number),
      a.road || a.pedestrian || a.footway,
      a.neighbourhood || a.suburb,
      a.city || a.town || a.village || a.county,
      a.state,
      a.postcode,
    ].filter(Boolean);

    const location = {
      name: result.name || label,
      address: addrParts.join(', ') || result.display_name,
      lat: parseFloat(result.lat),
      lng: parseFloat(result.lon),
    };
    setSearchText(location.name || location.address);
    setResults([]);
    setShowResults(false);
    onChange(location);
  };

  const handleGPS = () => {
    if (!navigator.geolocation) return;
    setGpsCapturing(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          const geo = await reverseGeocode(lat, lng);
          const location = {
            name: geo?.name || 'Current location',
            address: geo?.address || '',
            lat,
            lng,
          };
          setSearchText(location.name);
          onChange(location);
        } catch (e) {
          logger.warn('Reverse geocode failed:', e.message);
        } finally {
          setGpsCapturing(false);
        }
      },
      (err) => {
        logger.warn('GPS failed:', err.message);
        setGpsCapturing(false);
      },
      { timeout: 8000, maximumAge: 0 }
    );
  };

  const handleClear = () => {
    setSearchText('');
    setResults([]);
    setShowResults(false);
    onChange(null);
  };

  const mapsUrl = buildMapsUrl(value);
  const directionsUrl = buildDirectionsUrl(value);

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Search input */}
      <TextField
        fullWidth
        label={label}
        value={searchText}
        onChange={(e) => handleSearchChange(e.target.value)}
        onFocus={() => results.length > 0 && setShowResults(true)}
        onBlur={() => setTimeout(() => setShowResults(false), 200)}
        placeholder={placeholder}
        disabled={disabled}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LocationIcon fontSize="small" color={value ? 'primary' : 'disabled'} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {searching && <CircularProgress size={16} sx={{ mr: 0.5 }} />}
              {searchText && !searching && (
                <Tooltip title="Clear">
                  <IconButton size="small" onClick={handleClear} edge="end">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={gpsCapturing ? 'Getting location…' : 'Use my current location'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={handleGPS}
                    disabled={disabled || gpsCapturing || !navigator.geolocation}
                    color={value?.lat ? 'success' : 'default'}
                    edge="end"
                  >
                    {gpsCapturing ? <CircularProgress size={16} /> : <GPSIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </InputAdornment>
          ),
        }}
      />

      {/* Search results dropdown */}
      {showResults && results.length > 0 && (
        <Paper
          elevation={8}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1400,
            maxHeight: 260,
            overflowY: 'auto',
            mt: 0.5,
          }}
        >
          <List dense disablePadding>
            {results.map((result, i) => {
              const label = buildLabel(result);
              const subLabel = result.display_name?.split(',').slice(1, 3).join(',').trim();
              return (
                <React.Fragment key={result.place_id || i}>
                  <ListItem
                    button
                    onClick={() => handleSelect(result)}
                    sx={{ py: 1, '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <LocationIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary', flexShrink: 0 }} />
                    <ListItemText
                      primary={<Typography variant="body2" sx={{ fontWeight: 500 }}>{label}</Typography>}
                      secondary={subLabel && <Typography variant="caption" color="text.secondary" noWrap>{subLabel}</Typography>}
                    />
                  </ListItem>
                  {i < results.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        </Paper>
      )}

      {/* Selected location actions */}
      {value && (value.name || value.address) && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
          <Chip
            icon={<LocationIcon sx={{ fontSize: '0.8rem !important' }} />}
            label={value.name || value.address}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ maxWidth: 220, '.MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
          />
          {value.address && value.address !== value.name && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 180 }}>
              {value.address}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
            {mapsUrl && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<MapIcon sx={{ fontSize: '0.85rem !important' }} />}
                component="a"
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontSize: '0.72rem', py: 0.3, px: 1 }}
              >
                View
              </Button>
            )}
            {directionsUrl && (
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<DirectionsIcon sx={{ fontSize: '0.85rem !important' }} />}
                component="a"
                href={directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontSize: '0.72rem', py: 0.3, px: 1 }}
              >
                Directions
              </Button>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export { buildMapsUrl, buildDirectionsUrl };
export default LocationPicker;
